import React, { useEffect, useState, useCallback, useRef } from 'react';
import { tradingService } from '../../services/tradingService';
import InfusionManagerPanel from './InfusionManagerPanel';
import { useAuth } from '../../context/AuthContext';

// External Book: off-brokerage accounts (IBKRAccount.is_model). Create one, deploy
// strategies to it from the Optimizations tab exactly like a live/paper account, then
// either DOWNLOAD its daily orders as a Bloomberg Trade Template + UPLOAD the completed
// fill log (manual mode), or let it AUTO-fill at the next session open (auto mode).
// Performance/Risk treat it identically to an IBKR account. (Named "External Book" to
// avoid collision with the existing "Model Portfolio" net-weights panel.)

const fmtUsd = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
};

const MODE_LABEL = {
  manual_upload: 'Manual · Bloomberg download / upload',
  auto_next_open: 'Auto · fills at next open',
};

function CreateModelForm({ onCreated, showToast }) {
  const [name, setName] = useState('');
  const [mode, setMode] = useState('manual_upload');
  const [cash, setCash] = useState('5000000');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!name.trim()) { setErr('Name is required.'); return; }
    setBusy(true);
    try {
      const { data } = await tradingService.createModelAccount({
        name: name.trim(), model_fill_mode: mode, starting_cash: cash,
      });
      showToast(`Created external-book account “${data.name}”`);
      setName(''); setCash('5000000'); setMode('manual_upload');
      onCreated();
    } catch (e2) {
      setErr(e2?.response?.data?.error || 'Could not create the external-book account.');
    } finally { setBusy(false); }
  };

  const inputCls = 'w-full bg-transparent border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c9a96e]';
  return (
    <form onSubmit={submit} className="border border-gray-200 dark:border-gray-800 rounded-xl p-4 space-y-3">
      <div className="text-[11px] uppercase tracking-wider text-gray-500">New external-book account</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-[11px] text-gray-500 mb-1">Name</label>
          <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} maxLength={120} placeholder="e.g. Model Alpha" />
        </div>
        <div>
          <label className="block text-[11px] text-gray-500 mb-1">Fill mode</label>
          <select className={inputCls} value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="manual_upload">Manual (Bloomberg)</option>
            <option value="auto_next_open">Auto (next open)</option>
          </select>
        </div>
        <div>
          <label className="block text-[11px] text-gray-500 mb-1">Starting cash (USD)</label>
          <input className={inputCls} type="number" min="0" step="1000" value={cash} onChange={(e) => setCash(e.target.value)} />
        </div>
      </div>
      {err && <div className="text-xs text-red-500">{err}</div>}
      <div className="flex justify-end">
        <button type="submit" disabled={busy}
          className="px-4 py-1.5 rounded-lg text-sm text-white bg-[#c9a96e] hover:opacity-90 disabled:opacity-50">
          {busy ? 'Creating…' : 'Create external-book account'}
        </button>
      </div>
    </form>
  );
}

function UploadPanel({ account, onDone, showToast }) {
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [bucketMsg, setBucketMsg] = useState('');
  const inputRef = useRef(null);

  const run = async (dryRun) => {
    if (!file) { setErr('Choose a completed fill-log .xlsx first.'); return; }
    setErr(''); setBusy(true); setBucketMsg('');
    try {
      const { data } = await tradingService.uploadModelFills(account.id, file, dryRun);
      setReport({ ...data, dryRun });
      if (!dryRun) {
        showToast(`Applied ${data.applied} fill(s) · cash ${fmtUsd(data.cash_after)}`);
        onDone();
      }
    } catch (e2) {
      setErr(e2?.response?.data?.error || 'Upload failed.');
    } finally { setBusy(false); }
  };

  // Apply = the COMPLETE, correct action in ONE press: book the matched fills to their strategy
  // legs AND record any unmatched fills into the error bucket (model-only reconciliation suspense),
  // in one transaction (backend record-unmatched endpoint), then run generation so the bucket nets
  // off (final = staged − bucket). Idempotent (ticket-keyed) — safe to re-run. This replaces the
  // old split of "Apply fills" (matched only) + a separate "record to bucket" button, which made it
  // impossible to do both in one action.
  const applyAll = async () => {
    if (!file) { setErr('Choose a completed fill-log .xlsx first.'); return; }
    const nU = report?.unmatched?.length;
    if (!window.confirm(
      'Apply the matched fills AND record any unmatched fills to the error bucket, then run '
      + `generation to net it off?${nU ? ` (${nU} unmatched in the last preview)` : ''}\n\n`
      + 'Idempotent — safe if already partly done.')) return;
    setErr(''); setBusy(true); setBucketMsg('');
    try {
      const { data } = await tradingService.recordUnmatchedToBucket(account.id, file, false);
      setReport({ ...data, dryRun: false, applied: data.matched_applied ?? 0 });
      setBucketMsg(`Applied ${data.matched_applied || 0} matched · recorded ${data.applied || 0} to error bucket`
        + (data.matched_skipped_dup ? ` (${data.matched_skipped_dup} matched already applied)` : '')
        + '. Generation will net the bucket off.');
      showToast(`Applied ${data.matched_applied || 0} matched + ${data.applied || 0} to the error bucket.`);
      onDone();
    } catch (e2) {
      setErr(e2?.response?.data?.error || 'Apply failed.');
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 border-t border-gray-200 dark:border-gray-800 pt-3 space-y-2">
      <div className="text-[11px] uppercase tracking-wider text-gray-500">Upload completed fills</div>
      <div className="flex flex-wrap items-center gap-2">
        <input ref={inputRef} type="file" accept=".xlsx"
          onChange={(e) => { setFile(e.target.files?.[0] || null); setReport(null); }}
          className="text-xs" />
        <button onClick={() => run(true)} disabled={busy || !file}
          className="px-3 py-1 rounded-lg text-xs border border-gray-300 dark:border-gray-700 disabled:opacity-50">
          {busy ? '…' : 'Preview'}
        </button>
        <button onClick={applyAll} disabled={busy || !file}
          className="px-3 py-1 rounded-lg text-xs text-white bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50">
          {busy ? '…' : 'Apply fills + bucket unmatched'}
        </button>
      </div>
      {err && <div className="text-xs text-red-500">{err}</div>}
      {report && (
        <div className="text-xs bg-gray-50 dark:bg-gray-900/50 rounded-lg p-2 space-y-1">
          <div>{report.dryRun ? 'Preview' : 'Applied'}: <b>{report.applied ?? report.would_fill ?? 0}</b> fill(s)
            {report.skipped_duplicate ? ` · ${report.skipped_duplicate} already applied` : ''}
            {report.cash_after != null && !report.dryRun ? ` · cash → ${fmtUsd(report.cash_after)}` : ''}</div>
          {Array.isArray(report.unmatched) && report.unmatched.length > 0 && (
            <div className="text-amber-600">
              <div className="font-semibold mb-1">
                {report.unmatched.length} unmatched — no open staged order for this symbol+side
                (surfaced, not guessed):
              </div>
              <div className="max-h-52 overflow-auto scrollbar-thin rounded border border-amber-200 dark:border-amber-900/40">
                <table className="w-full text-[11px]">
                  <thead className="sticky top-0 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400">
                    <tr className="text-left">
                      <th className="px-2 py-1 font-medium">Symbol</th>
                      <th className="px-2 py-1 font-medium">Side</th>
                      <th className="px-2 py-1 font-medium text-right">Qty</th>
                      <th className="px-2 py-1 font-medium">As of</th>
                      <th className="px-2 py-1 font-medium">Reason</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700 dark:text-gray-300">
                    {report.unmatched.map((u, i) => (
                      <tr key={u.ticket || i} className="border-t border-amber-100 dark:border-amber-900/20">
                        <td className="px-2 py-0.5 font-semibold">{u.symbol}</td>
                        <td className={`px-2 py-0.5 ${u.side === 'BUY' ? 'text-emerald-600' : 'text-red-500'}`}>{u.side}</td>
                        <td className="px-2 py-0.5 text-right tabular-nums">{u.quantity}</td>
                        <td className="px-2 py-0.5">{u.as_of || '—'}</td>
                        <td className="px-2 py-0.5 text-gray-500">{u.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-2 text-[10px] text-gray-500">
                These unmatched fills are recorded to the error bucket when you press
                <b> Apply fills + bucket unmatched</b> above — one action books the matched fills to
                strategies AND buckets the unmatched, then runs generation to net it off
                (final = staged − bucket). Idempotent — safe to re-run.
              </div>
              {bucketMsg && <div className="mt-1 text-emerald-600">{bucketMsg}</div>}
            </div>
          )}
          {Array.isArray(report.skipped_rows) && report.skipped_rows.length > 0 && (
            <div className="text-gray-500">{report.skipped_rows.length} row(s) skipped (cancelled / malformed)</div>
          )}
        </div>
      )}
    </div>
  );
}

// Read-only view of the account's error bucket (unmatched fills recorded to reconciliation
// suspense) — net shares, cost, and P&L marked to latest close. Nets off on the next generation.
// Reconciliation upload — a SAFE INBOX. Stores a raw fills/positions workbook verbatim for a
// by-hand reconcile; never matches, applies, or touches strategies / positions / the error bucket.
function ReconcileUploadPanel({ account, showToast }) {
  const [file, setFile] = useState(null);
  const [kind, setKind] = useState('fills');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [uploads, setUploads] = useState([]);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await tradingService.listReconcileUploads(account.id);
      setUploads(data?.uploads || []);
    } catch { /* ignore */ }
  };

  const upload = async () => {
    if (!file) { setErr('Choose an .xlsx first.'); return; }
    setErr(''); setMsg(''); setBusy(true);
    try {
      const { data } = await tradingService.reconcileUpload(account.id, file, kind);
      setMsg(`Stored ${data.row_count} ${kind} row(s) as upload #${data.id}. NOT applied — staged for reconciliation.`);
      showToast(`Stored ${data.row_count} ${kind} row(s) for reconciliation.`);
      setFile(null);
      load();
    } catch (e2) {
      setErr(e2?.response?.data?.error || 'Upload failed.');
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 border-t border-gray-200 dark:border-gray-800 pt-3">
      <button onClick={() => { const n = !open; setOpen(n); if (n) load(); }}
        className="text-[11px] uppercase tracking-wider text-gray-500 hover:text-[#c9a96e]">
        {open ? '▾' : '▸'} Reconciliation upload (raw — not applied)
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          <div className="text-[11px] text-gray-500">
            Stores a raw fills or positions workbook <b>verbatim</b> for a by-hand reconcile. It does
            NOT match, apply, or touch strategies / positions / cash / the error bucket.
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select value={kind} onChange={(e) => setKind(e.target.value)}
              className="text-xs rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-1">
              <option value="fills">Fills</option>
              <option value="positions">Positions</option>
            </select>
            <input type="file" accept=".xlsx"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setMsg(''); setErr(''); }}
              className="text-xs" />
            <button onClick={upload} disabled={busy || !file}
              className="px-3 py-1 rounded-lg text-xs text-white bg-[#c9a96e] hover:opacity-90 disabled:opacity-50">
              {busy ? '…' : 'Upload (store only)'}
            </button>
          </div>
          {msg && <div className="text-xs text-emerald-600">{msg}</div>}
          {err && <div className="text-xs text-red-500">{err}</div>}
          {uploads.length > 0 && (
            <div className="text-[11px] text-gray-500">
              <div className="uppercase tracking-wider mb-1">Recent uploads</div>
              <div className="space-y-0.5">
                {uploads.map((u) => (
                  <div key={u.id} className="flex gap-2 tabular-nums">
                    <span className="text-gray-400">#{u.id}</span>
                    <span className="w-16">{u.kind}</span>
                    <span className="w-16 text-right">{u.row_count} rows</span>
                    <span className="text-gray-400">{new Date(u.uploaded_at).toLocaleString()}</span>
                    {u.consumed && <span className="text-emerald-600">✓ reconciled</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function ErrorBucketPanel({ account, showToast }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await tradingService.getErrorBucket(account.id);
      setData(d);
    } catch { /* panel just stays hidden on error */ }
  }, [account.id]);
  useEffect(() => { load(); }, [load]);

  const reverse = async (symbol) => {
    if (!window.confirm(`Reverse (un-record) all error-bucket entries for ${symbol}?`)) return;
    setBusy(true);
    try {
      await tradingService.reverseErrorBucket(account.id, symbol);
      showToast(`Reversed ${symbol} from the error bucket.`);
      await load();
    } catch (e) {
      showToast(e?.response?.data?.error || 'Could not reverse.', 'error');
    } finally { setBusy(false); }
  };

  const entries = data?.entries || [];
  if (!entries.length) return null;
  const pnlColor = (v) => (v > 0 ? 'text-emerald-600' : v < 0 ? 'text-red-500' : 'text-gray-500');
  return (
    <div className="mt-3 border-t border-gray-200 dark:border-gray-800 pt-3">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-amber-600">
          Error bucket · {data.open_count} open
        </div>
        <button onClick={load} className="text-[10px] text-gray-400 hover:text-gray-600">↻ refresh</button>
      </div>
      <div className="mt-1 text-[11px] text-gray-500">
        Total P&amp;L <span className={pnlColor(data.total_pnl)}>{fmtUsd(data.total_pnl)}</span>
        {' '}(unreal <span className={pnlColor(data.total_unrealized)}>{fmtUsd(data.total_unrealized)}</span>
        {' '}· real <span className={pnlColor(data.total_realized)}>{fmtUsd(data.total_realized)}</span>).
        Nets off on the next generation.
      </div>
      <div className="mt-1 max-h-52 overflow-auto scrollbar-thin rounded border border-gray-200 dark:border-gray-800">
        <table className="w-full text-[11px]">
          <thead className="sticky top-0 bg-gray-50 dark:bg-gray-900/50 text-gray-500">
            <tr className="text-left">
              <th className="px-2 py-1 font-medium">Symbol</th>
              <th className="px-2 py-1 font-medium text-right">Net sh</th>
              <th className="px-2 py-1 font-medium text-right">Avg</th>
              <th className="px-2 py-1 font-medium text-right">Last</th>
              <th className="px-2 py-1 font-medium text-right">Unreal</th>
              <th className="px-2 py-1 font-medium text-right">Real</th>
              <th className="px-2 py-1 font-medium"></th>
            </tr>
          </thead>
          <tbody className="text-gray-700 dark:text-gray-300">
            {entries.map((e) => (
              <tr key={e.symbol} className="border-t border-gray-100 dark:border-gray-800/60">
                <td className="px-2 py-0.5 font-semibold">
                  {e.symbol}{!e.is_open && <span className="ml-1 text-[9px] text-gray-400">closed</span>}
                </td>
                <td className={`px-2 py-0.5 text-right tabular-nums ${e.net_shares < 0 ? 'text-red-500' : ''}`}>{e.net_shares}</td>
                <td className="px-2 py-0.5 text-right tabular-nums">{e.avg_price?.toFixed(2)}</td>
                <td className="px-2 py-0.5 text-right tabular-nums">{e.last_price != null ? e.last_price.toFixed(2) : '—'}</td>
                <td className={`px-2 py-0.5 text-right tabular-nums ${pnlColor(e.unrealized_pnl)}`}>{fmtUsd(e.unrealized_pnl)}</td>
                <td className={`px-2 py-0.5 text-right tabular-nums ${pnlColor(e.realized_pnl)}`}>{fmtUsd(e.realized_pnl)}</td>
                <td className="px-2 py-0.5 text-right">
                  {e.is_open && (
                    <button onClick={() => reverse(e.symbol)} disabled={busy}
                      className="text-[10px] text-gray-400 hover:text-red-500 disabled:opacity-50">reverse</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BloombergDefaults({ account, onSaved, showToast }) {
  // Per-account default text stamped into the exported Bloomberg Trade Template's
  // Broker / Prime Broker / Long Note 1 columns. Set once, pre-fills every download.
  const [acctCode, setAcctCode] = useState(account.bb_account || '');
  const [broker, setBroker] = useState(account.bb_broker || '');
  const [prime, setPrime] = useState(account.bb_prime_broker || '');
  const [note, setNote] = useState(account.bb_long_note || '');
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const dirty = acctCode !== (account.bb_account || '') ||
                broker !== (account.bb_broker || '') ||
                prime !== (account.bb_prime_broker || '') ||
                note !== (account.bb_long_note || '');

  const save = async () => {
    setBusy(true);
    try {
      await tradingService.updateIBKRAccount(account.id, {
        bb_account: acctCode.trim(),
        bb_broker: broker.trim(), bb_prime_broker: prime.trim(), bb_long_note: note.trim(),
      });
      showToast('Saved Bloomberg template defaults.');
      onSaved();
    } catch (e) {
      showToast(e?.response?.data?.error || 'Could not save defaults.', 'error');
    } finally { setBusy(false); }
  };

  const inputCls = 'w-full bg-transparent border border-gray-300 dark:border-gray-700 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:border-[#c9a96e]';
  const summary = [account.bb_broker, account.bb_prime_broker].filter(Boolean).join(' · ') || 'not set';

  return (
    <div className="mt-3 border-t border-gray-200 dark:border-gray-800 pt-3">
      <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 text-left">
        <span className={`text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`}>›</span>
        <span className="text-[11px] uppercase tracking-wider text-gray-500">Bloomberg template defaults</span>
        <span className="text-[11px] text-gray-400">({summary})</span>
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Account (Bloomberg code)</label>
            <input className={inputCls} value={acctCode} onChange={(e) => setAcctCode(e.target.value)} maxLength={64}
              placeholder={`blank = "${account.name}"`} />
            <div className="text-[10px] text-gray-400 mt-0.5">Fills the sheet's ‘Account’ column. Blank uses the display name.</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="block text-[11px] text-gray-500 mb-1">Broker</label>
              <input className={inputCls} value={broker} onChange={(e) => setBroker(e.target.value)} maxLength={64} placeholder="e.g. NTRC" />
            </div>
            <div>
              <label className="block text-[11px] text-gray-500 mb-1">Prime Broker</label>
              <input className={inputCls} value={prime} onChange={(e) => setPrime(e.target.value)} maxLength={64} placeholder="e.g. QCNT" />
            </div>
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Long Note 1</label>
            <input className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} maxLength={255} placeholder="e.g. Buy at open, market order" />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-gray-400">Stamped into every downloaded order sheet. Blank = leave the column empty.</span>
            <button onClick={save} disabled={busy || !dirty}
              className="px-3 py-1 rounded-lg text-xs text-white bg-[#c9a96e] hover:opacity-90 disabled:opacity-40">
              {busy ? 'Saving…' : 'Save defaults'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AutoEmailPanel({ account, showToast }) {
  // AUTOMATED order email: after the 05:00 ET pre-open check completes, the staged-order CSV
  // is emailed to these recipients (with the instructions) — no manual step. Toggle + edit here;
  // "Send test now" fires the exact same email immediately to the SAME recipients (even if off).
  const [enabled, setEnabled] = useState(false);
  const [recipients, setRecipients] = useState('');   // comma/newline separated in the textarea
  const [instructions, setInstructions] = useState('');
  const [loaded, setLoaded] = useState(null);         // server snapshot, for dirty-check
  const [busy, setBusy] = useState('');
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await tradingService.getOrderEmailSettings(account.id);
      setEnabled(!!data.enabled);
      setRecipients((data.recipients || []).join(', '));
      setInstructions(data.instructions || '');
      setLoaded(data);
    } catch (e) { /* delegate-read allowed; leave defaults on error */ }
  }, [account.id]);
  useEffect(() => { load(); }, [load]);

  const parsedRecipients = recipients.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
  const dirty = loaded && (
    enabled !== !!loaded.enabled ||
    parsedRecipients.join(',') !== (loaded.recipients || []).join(',') ||
    instructions !== (loaded.instructions || ''));

  const save = async () => {
    setBusy('save');
    try {
      const { data } = await tradingService.updateOrderEmailSettings(account.id, {
        enabled, recipients: parsedRecipients, instructions,
      });
      setLoaded(data);
      showToast('Saved automated-email settings.');
    } catch (e) {
      showToast(e?.response?.data?.error || 'Could not save settings.', 'error');
    } finally { setBusy(''); }
  };

  const sendTest = async () => {
    if (!parsedRecipients.length) { showToast('Add at least one recipient first.', 'error'); return; }
    if (dirty) { showToast('Save your changes before sending a test.', 'error'); return; }
    setBusy('test');
    try {
      const { data } = await tradingService.sendOrderEmailNow(account.id);
      showToast(`Sent ${data.legs}-leg order email to ${(data.recipients || []).join(', ')}.`);
    } catch (e) {
      showToast(e?.response?.data?.error || 'Could not send the test email.', 'error');
    } finally { setBusy(''); }
  };

  const inputCls = 'w-full bg-transparent border border-gray-300 dark:border-gray-700 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:border-[#c9a96e]';
  const nRcpt = parsedRecipients.length;
  const summary = enabled ? `on · ${nRcpt} recipient${nRcpt === 1 ? '' : 's'}` : 'off';

  return (
    <div className="mt-3 border-t border-gray-200 dark:border-gray-800 pt-3">
      <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 text-left">
        <span className={`text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`}>›</span>
        <span className="text-[11px] uppercase tracking-wider text-gray-500">Automated order email</span>
        <span className={`text-[11px] ${enabled ? 'text-emerald-600' : 'text-gray-400'}`}>({summary})</span>
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)}
              className="accent-[#c9a96e]" />
            <span className="text-sm">Email the staged-order CSV after the 05:00 ET pre-open check</span>
          </label>
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Recipients (comma-separated)</label>
            <input className={inputCls} value={recipients} onChange={(e) => setRecipients(e.target.value)}
              type="text" autoComplete="off" placeholder="joji@abudhabicapital.ae, mahroof@abudhabicapital.ae" />
            <div className="text-[10px] text-gray-400 mt-0.5">Up to 10 addresses. The CSV attached is byte-identical to a manual download.</div>
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Instructions (email body)</label>
            <textarea className={`${inputCls} h-20 resize-y`} value={instructions} maxLength={2000}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="e.g. Attached are today's model-portfolio orders. Please execute at the open in Bloomberg and upload the completed fills." />
          </div>
          <div className="flex items-center justify-between gap-2">
            <button onClick={sendTest} disabled={!!busy || !nRcpt}
              title="Send the same email right now to the configured recipients (works even while the toggle is off)."
              className="px-3 py-1 rounded-lg text-xs border border-gray-300 dark:border-gray-700 hover:border-[#c9a96e] disabled:opacity-40">
              {busy === 'test' ? 'Sending…' : '✉ Send test now'}
            </button>
            <button onClick={save} disabled={!!busy || !dirty}
              className="px-3 py-1 rounded-lg text-xs text-white bg-[#c9a96e] hover:opacity-90 disabled:opacity-40">
              {busy === 'save' ? 'Saving…' : 'Save settings'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function EmailPanel({ account, showToast, onClose }) {
  // Email the current Bloomberg order sheet (staged orders) to any recipient(s). Read-only:
  // sending never changes order state (same set as Download). Recipients are comma-separated.
  const [to, setTo] = useState('');
  const [note, setNote] = useState('');
  const [format, setFormat] = useState('xlsx'); // 'xlsx' | 'csv'
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!to.trim()) { showToast('Enter at least one recipient email.', 'error'); return; }
    setBusy(true);
    try {
      const { data } = await tradingService.emailModelOrders(account.id, { to: to.trim(), note: note.trim(), format });
      showToast(`Emailed ${data.legs}-leg order sheet to ${data.to.join(', ')}.`);
      setTo(''); setNote(''); onClose();
    } catch (e) {
      showToast(e?.response?.data?.error || 'Could not send the email.', 'error');
    } finally { setBusy(false); }
  };

  const inputCls = 'w-full bg-transparent border border-gray-300 dark:border-gray-700 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:border-[#c9a96e]';
  return (
    <div className="mt-3 border-t border-gray-200 dark:border-gray-800 pt-3 space-y-2">
      <div className="text-[11px] uppercase tracking-wider text-gray-500">Email order sheet</div>
      <div>
        <label className="block text-[11px] text-gray-500 mb-1">To (comma-separated)</label>
        <input className={inputCls} value={to} onChange={(e) => setTo(e.target.value)} type="text"
          placeholder="trader@firm.com, ops@firm.com" autoComplete="off" />
      </div>
      <div>
        <label className="block text-[11px] text-gray-500 mb-1">Note (optional)</label>
        <input className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000}
          placeholder="e.g. Please execute at the open." />
      </div>
      <div>
        <label className="block text-[11px] text-gray-500 mb-1">Attachment format</label>
        <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-700 overflow-hidden text-xs">
          {['xlsx', 'csv'].map((f) => (
            <button key={f} onClick={() => setFormat(f)}
              className={`px-3 py-1 ${format === f ? 'bg-[#c9a96e] text-white' : 'text-gray-500 hover:text-[#c9a96e]'}`}>
              .{f}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-gray-400">Sends the same sheet as Download, attached. Order state is unchanged.</span>
        <div className="flex gap-2">
          <button onClick={onClose} disabled={busy}
            className="px-3 py-1 rounded-lg text-xs border border-gray-300 dark:border-gray-700 disabled:opacity-40">Cancel</button>
          <button onClick={send} disabled={busy || !to.trim()}
            className="px-3 py-1 rounded-lg text-xs text-white bg-[#c9a96e] hover:opacity-90 disabled:opacity-40">
            {busy ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ModelAccountCard({ account, onChanged, showToast }) {
  const [downloading, setDownloading] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [busy, setBusy] = useState(null); // null | 'nofill' | 'regen'
  const isManual = account.model_fill_mode !== 'auto_next_open';
  const pending = !!account.has_unresolved_batch;

  const noFills = async () => {
    if (!window.confirm('Confirm NO fills were traded for the current staged batch? This runs the same '
      + 'pipeline as a fills upload: before the US open (or after 22:00 ET) the drift check, the rebalance if due '
      + 'and a new sheet are built now (1–3 hours); during US trading hours the 19:30 ET evening run builds it.')) return;
    setBusy('nofill');
    try {
      const { data } = await tradingService.modelNoFills(account.id);
      showToast(data?.reason || 'No-fills confirmed — generation unlocked.');
      onChanged();
    } catch (e) {
      showToast(e?.response?.data?.error || 'Could not confirm no-fills.', 'error');
    } finally { setBusy(null); }
  };

  const regenerate = async () => {
    if (pending && !window.confirm('A staged batch is still pending reconciliation. Regenerating '
      + 'now will SUPERSEDE it. Continue?')) return;
    setBusy('regen');
    try {
      const res = await tradingService.regenerateModelSignals(account.id);
      showToast(res?.data?.held_until
        ? 'Regeneration held until 09:31 ET so the live account\u2019s pre-open release goes first — the staged sheet refreshes after that.'
        : 'Regeneration started — the staged sheet will refresh in a few minutes.');
      onChanged();
    } catch (e) {
      const msg = e?.response?.status === 409
        ? (e?.response?.data?.error || 'A generation for this account is already in progress.')
        : (e?.response?.data?.error || 'Could not start regeneration.');
      showToast(msg, 'error');
    } finally { setBusy(null); }
  };

  const download = async (format = 'xlsx') => {
    setDownloading(true);
    try {
      const resp = await tradingService.downloadModelOrders(account.id, true, format);
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `model_orders_${account.name}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      showToast('Downloaded order sheet — re-downloadable anytime. Upload fills when executed.');
    } catch (e) {
      // Blob error responses arrive as a Blob, not JSON — read the text to surface the message.
      let msg = 'Download failed.';
      try {
        const blob = e?.response?.data;
        if (blob && typeof blob.text === 'function') { const j = JSON.parse(await blob.text()); msg = j.error || msg; }
      } catch { /* keep default */ }
      showToast(msg, 'error');
    } finally { setDownloading(false); }
  };

  return (
    <div className="border border-gray-200 dark:border-gray-800 rounded-xl p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="font-serif text-lg">{account.name}</div>
          <div className="text-[11px] text-gray-500">{account.account_id} · {MODE_LABEL[account.model_fill_mode] || account.model_fill_mode}</div>
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-wider text-gray-500">Cash</div>
          <div className="text-emerald-600 font-medium tabular-nums">{fmtUsd(account.cash_usd)}</div>
        </div>
      </div>
      {isManual ? (
        <>
          {pending && (
            <div className="mt-3 rounded-lg border border-amber-400/60 bg-amber-50 dark:bg-amber-900/20 px-3 py-2">
              <div className="text-[11px] uppercase tracking-wider text-amber-700 dark:text-amber-400 font-medium">
                Batch pending reconciliation
              </div>
              <div className="text-xs text-amber-800 dark:text-amber-200 mt-0.5">
                A staged order sheet is awaiting your fills{account.pending_since ? ` (since ${new Date(account.pending_since).toLocaleString()})` : ''}.
                The scheduled 19:30 ET regeneration is <b>paused</b> for this account so it can’t overwrite the sheet you’re trading.
                Upload the fills below, or confirm no fills were traded.
              </div>
              <button onClick={noFills} disabled={busy === 'nofill'}
                className="mt-2 px-3 py-1 rounded-lg text-xs border border-amber-500 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40 disabled:opacity-50">
                {busy === 'nofill' ? 'Confirming…' : 'No fills today'}
              </button>
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <button onClick={() => download('xlsx')} disabled={downloading}
              className="px-3 py-1 rounded-lg text-xs text-white bg-[#c9a96e] hover:opacity-90 disabled:opacity-50">
              {downloading ? 'Preparing…' : 'Download orders (Bloomberg .xlsx)'}
            </button>
            <button onClick={() => download('csv')} disabled={downloading}
              className="px-3 py-1 rounded-lg text-xs border border-gray-300 dark:border-gray-700 hover:border-[#c9a96e] disabled:opacity-50">
              Download .csv
            </button>
            <button onClick={() => setEmailOpen((o) => !o)}
              className={`px-3 py-1 rounded-lg text-xs border transition-colors ${emailOpen ? 'border-[#c9a96e] text-[#c9a96e]' : 'border-gray-300 dark:border-gray-700 hover:border-[#c9a96e]'}`}>
              ✉ Email
            </button>
            <button onClick={regenerate} disabled={busy === 'regen'}
              title="Recompute this account's staged order sheet now (supersedes the current batch)."
              className="px-3 py-1 rounded-lg text-xs border border-gray-300 dark:border-gray-700 hover:border-[#c9a96e] disabled:opacity-50">
              {busy === 'regen' ? 'Regenerating…' : '↻ Regenerate signals'}
            </button>
          </div>
          {emailOpen && <EmailPanel account={account} showToast={showToast} onClose={() => setEmailOpen(false)} />}
          <BloombergDefaults account={account} onSaved={onChanged} showToast={showToast} />
          <AutoEmailPanel account={account} showToast={showToast} />
          <UploadPanel account={account} onDone={onChanged} showToast={showToast} />
          <ErrorBucketPanel account={account} showToast={showToast} />
          <ReconcileUploadPanel account={account} showToast={showToast} />
        </>
      ) : (
        <div className="mt-3 text-xs text-gray-500">
          Auto mode — staged orders fill automatically at the next session open. No download/upload needed.
        </div>
      )}
      <div className="mt-4">
        <InfusionManagerPanel accountId={account.id} accountName={account.name} />
      </div>
    </div>
  );
}

export default function ExternalBookPage() {
  const { user } = useAuth();
  // A delegated operator manages an EXISTING external book — they never create new accounts.
  const isOperator = !!(user?.trading_delegate?.is_delegate) && !user?.is_admin;
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((msg, kind = 'ok') => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await tradingService.listIBKRAccounts();
      // /ibkr-accounts/ is DRF-paginated ({count, results}); handle both shapes.
      const list = Array.isArray(data) ? data : (data?.results || []);
      setAccounts(list.filter((a) => a.is_model));
    } catch {
      showToast('Could not load external-book accounts.', 'error');
    } finally { setLoading(false); }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 space-y-4">
      <div>
        <h1 className="font-serif text-2xl">External Book</h1>
        <p className="text-xs text-gray-500 mt-1 max-w-2xl">
          Off-brokerage accounts — not connected to IBKR. Deploy strategies to them from the
          Optimizations tab just like a live/paper account; they appear in Performance and Risk
          identically. Manual mode round-trips orders through Bloomberg; auto mode fills at the
          next open.
        </p>
      </div>

      {!isOperator && <CreateModelForm onCreated={load} showToast={showToast} />}

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : accounts.length === 0 ? (
        <div className="text-sm text-gray-500">No external-book accounts yet. Create one above.</div>
      ) : (
        <div className="space-y-3">
          {accounts.map((a) => (
            <ModelAccountCard key={a.id} account={a} onChanged={load} showToast={showToast} />
          ))}
        </div>
      )}

      {toast && (
        <div className={`fixed bottom-4 right-4 px-4 py-2 rounded-lg text-sm text-white shadow-lg ${toast.kind === 'error' ? 'bg-red-600' : 'bg-gray-800'}`}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
